import { FormInstance, message } from 'antd';
import { NavigateFunction } from 'react-router-dom';
import { ApiService } from 'service/api/apiService';
import { peopleApiService } from 'service/api/peopleApiService';
import { PeopleInterface } from 'type/peopleType';
import { CastFormData } from '../../../../shared/types/i18n/i18nTypes';
import {
  createCastApiRequest,
  validateCastRequiredFields,
} from '../../../../shared/services/i18n/i18nService';

class PeopleService {
  constructor(private apiService: ApiService<PeopleInterface>) {}

  public async createPeople(form: FormInstance, navigate: NavigateFunction): Promise<void> {
    try {
      const peopleFormData = this.extractPeopleFromForm(form);
      await this.apiService.createByFormData(peopleFormData);
      navigate('/people');
    } catch (error) {
      message.error('Failed to create : ' + error);
    }
  }

  public async updatePeople(
    id: number,
    form: FormInstance,
    navigate: NavigateFunction
  ): Promise<void> {
    try {
      const peopleFormData = this.extractPeopleFromForm(form);
      await this.apiService.updateByFormData(id, peopleFormData);
      message.success('Updated successfully');
      navigate('/people');
    } catch (error) {
      message.error('Failed to update : ' + error);
    }
  }

  /**
   * Cast 생성 (i18n 지원)
   */
  public async createCastWithI18n(
    castData: CastFormData,
    navigate: NavigateFunction
  ): Promise<void> {
    try {
      // 필수 필드 검증
      const validationErrors = validateCastRequiredFields(castData);
      if (validationErrors.length > 0) {
        const errorMessage = validationErrors.map((error: any) => error.message).join(', ');
        message.error(`검증 오류: ${errorMessage}`);
        return;
      }

      // API 요청 데이터 생성
      const apiRequest = createCastApiRequest(castData);
      await peopleApiService.createCastWithI18n(apiRequest);

      message.success('Cast가 성공적으로 생성되었습니다');
      navigate('/people');
    } catch (error) {
      message.error('Cast 생성 실패: ' + error);
    }
  }

  /**
   * Cast 업데이트 (i18n 지원)
   */
  public async updateCastWithI18n(
    id: number,
    castData: CastFormData,
    navigate: NavigateFunction
  ): Promise<void> {
    try {
      // 필수 필드 검증
      const validationErrors = validateCastRequiredFields(castData);
      if (validationErrors.length > 0) {
        const errorMessage = validationErrors.map((error: any) => error.message).join(', ');
        message.error(`검증 오류: ${errorMessage}`);
        return;
      }

      // API 요청 데이터 생성
      const apiRequest = createCastApiRequest(castData);
      await peopleApiService.updateCastWithI18n(id, apiRequest);

      message.success('Cast가 성공적으로 업데이트되었습니다');
      navigate('/people');
    } catch (error) {
      message.error('Cast 업데이트 실패: ' + error);
    }
  }

  private extractPeopleFromForm(form: FormInstance): FormData {
    const formData = new FormData();
    formData.append('name', form.getFieldValue('name'));
    formData.append('slug', form.getFieldValue('slug'));
    if (form.getFieldValue('name_en')) {
      formData.append('name_en', form.getFieldValue('name_en'));
    }
    if (form.getFieldValue('birthdate')) {
      formData.append('birthdate', form.getFieldValue('birthdate'));
    }
    if (form.getFieldValue('nationality')) {
      formData.append('nationality', form.getFieldValue('nationality'));
    }
    if (form.getFieldValue('thumbnail')) {
      formData.append('thumbnail', form.getFieldValue('thumbnail'));
    }
    if (form.getFieldValue('type')) {
      formData.append('type', form.getFieldValue('type'));
    }
    return formData;
  }
}

export const peopleService = new PeopleService(peopleApiService);
